import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SafeArea, Toast, Dialog } from 'antd-mobile';
import { useNavigate } from 'react-router-dom';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    rectSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useStore } from '../store';
import { api } from '../api';
import { Plus, Calculator, QrCode, Edit2, CheckCircle2, Save } from 'lucide-react';
import tangtungChef from '../assets/tangtung-chef-empty.png';
import './MiniPosPage.css';

// Types
interface MenuItem {
    id: number;
    name: string;
    price: number;
    image_url: string | null;
    sort_order: number;
}

interface CartItem {
    menuItem: MenuItem;
    quantity: number;
}

// Sortable Menu Card Component
function SortableMenuCard({
    item,
    isEditMode,
    isSelected,
    quantity,
    onSelect,
    onDelete,
    onEdit,
}: {
    item: MenuItem;
    isEditMode: boolean;
    isSelected: boolean;
    quantity: number;
    onSelect: () => void;
    onDelete: () => void;
    onEdit: () => void;
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: item.id,
        disabled: !isEditMode,
        transition: {
            duration: 250,
            easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
        },
    });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition: transition || 'transform 250ms cubic-bezier(0.25, 1, 0.5, 1)',
        opacity: isDragging ? 0.5 : 1,
        touchAction: isEditMode ? 'none' : 'auto',
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`menu-card ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${isEditMode ? 'edit-mode' : ''}`}
            onClick={!isEditMode ? onSelect : undefined}
            {...(isEditMode ? { ...attributes, ...listeners } : {})}
        >
            {/* Drag Handle - visible in edit mode */}
            {isEditMode && (
                <div className="drag-handle">
                    <span>⋮⋮</span>
                </div>
            )}

            <div className="menu-card-image">
                {item.image_url ? (
                    <img src={item.image_url} alt={item.name} loading="lazy" />
                ) : (
                    <span className="menu-card-emoji">🍽️</span>
                )}
            </div>
            <div className="menu-card-info">
                <div className="menu-card-name">{item.name}</div>
                <div className="menu-card-price">฿{item.price.toLocaleString()}</div>
            </div>
            {quantity > 0 && !isEditMode && (
                <div className="menu-card-badge">{quantity}</div>
            )}
            {isEditMode && (
                <div className="menu-card-edit-buttons">
                    <button
                        className="edit-btn edit"
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); onEdit(); }}
                        onPointerDown={(e) => e.stopPropagation()}
                    >
                        ✏️
                    </button>
                    <button
                        className="edit-btn delete"
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); onDelete(); }}
                        onPointerDown={(e) => e.stopPropagation()}
                    >
                        🗑️
                    </button>
                </div>
            )}
        </div>
    );
}

// Drag Overlay Card (shown while dragging)
function DragOverlayCard({ item }: { item: MenuItem }) {
    return (
        <div className="menu-card dragging-overlay">
            <div className="menu-card-image">
                {item.image_url ? (
                    <img src={item.image_url} alt={item.name} />
                ) : (
                    <span className="menu-card-emoji">🍽️</span>
                )}
            </div>
            <div className="menu-card-info">
                <div className="menu-card-name">{item.name}</div>
                <div className="menu-card-price">฿{item.price.toLocaleString()}</div>
            </div>
        </div>
    );
}

// Add Menu Modal Component
function AddMenuModal({
    isOpen,
    onClose,
    onSave,
    onImageUpload,
    editItem,
    isUploading,
}: {
    isOpen: boolean;
    onClose: () => void;
    onSave: (name: string, price: number, imageUrl: string | null) => void;
    onImageUpload: (file: File) => Promise<string | null>;
    editItem: MenuItem | null;
    isUploading: boolean;
}) {
    const [name, setName] = useState('');
    const [price, setPrice] = useState('');
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [localPreview, setLocalPreview] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen) {
            if (editItem) {
                setName(editItem.name);
                setPrice(editItem.price.toString());
                setImageUrl(editItem.image_url);
                setLocalPreview(null);
            } else {
                setName('');
                setPrice('');
                setImageUrl(null);
                setLocalPreview(null);
            }
        }
    }, [editItem, isOpen]);

    const handleImageClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Show local preview immediately
        const reader = new FileReader();
        reader.onload = (event) => {
            setLocalPreview(event.target?.result as string);
        };
        reader.readAsDataURL(file);

        // Upload to server
        const uploadedUrl = await onImageUpload(file);
        if (uploadedUrl) {
            setImageUrl(uploadedUrl);
            setLocalPreview(null);
        }
    };

    const handleSave = () => {
        if (!name.trim() || !price) {
            Toast.show({ content: 'กรุณากรอกชื่อและราคา', icon: 'fail' });
            return;
        }
        onSave(name.trim(), parseFloat(price), imageUrl);
    };

    if (!isOpen) return null;

    const displayImage = localPreview || imageUrl;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <div className="modal-title">{editItem ? 'แก้ไขเมนู' : 'เพิ่มเมนูใหม่'}</div>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="modal-body compact-layout">
                    <div className="form-row">
                        <div className="form-col image-col">
                            <label className="form-label">รูปภาพ</label>
                            <div
                                className={`image-upload compact ${displayImage ? 'has-image' : ''} ${isUploading ? 'uploading' : ''}`}
                                onClick={handleImageClick}
                            >
                                {displayImage ? (
                                    <>
                                        <img src={displayImage} alt="Preview" />
                                        {isUploading && <div className="upload-progress">Wait...</div>}
                                    </>
                                ) : (
                                    <>
                                        <div className="image-upload-icon">📷</div>
                                        <div className="image-upload-text">เพิ่มรูป</div>
                                    </>
                                )}
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />
                        </div>
                        <div className="form-col inputs-col">
                            <div className="form-group">
                                <label className="form-label">ชื่อเมนู</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="เช่น ก๋วยเตี๋ยว"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">ราคา (บาท)</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    placeholder="0"
                                    value={price}
                                    onChange={(e) => setPrice(e.target.value)}
                                    inputMode="decimal"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="modal-footer">
                    <button
                        className="modal-save-btn"
                        onClick={handleSave}
                        disabled={isUploading}
                    >
                        {isUploading ? 'กำลังอัปโหลด...' : editItem ? 'บันทึกการแก้ไข' : 'เพิ่มเมนู'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Minimal Confirmation Modal Component
function EditConfirmModal({
    isOpen,
    onClose,
    onConfirm,
    mode = 'enter' // 'enter' | 'exit'
}: {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    mode?: 'enter' | 'exit';
}) {
    if (!isOpen) return null;

    const isExit = mode === 'exit';

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content minimal-confirm" onClick={(e) => e.stopPropagation()}>
                <div className="confirm-icon-wrapper">
                    {isExit ? (
                        <CheckCircle2 size={32} color="var(--primary)" />
                    ) : (
                        <Edit2 size={32} color="var(--primary)" />
                    )}
                </div>
                <h3>{isExit ? 'บันทึกการแก้ไข?' : 'จัดการเมนู'}</h3>
                <p>
                    {isExit
                        ? 'ต้องการออกจากโหมดแก้ไขและ\nกลับสู่หน้าขายสินค้าใช่หรือไม่?'
                        : 'ต้องการเข้าสู่โหมดแก้ไขเพื่อ\nเพิ่ม ลบ หรือจัดเรียงเมนู?'}
                </p>
                <div className="confirm-actions">
                    <button className="confirm-btn cancel" onClick={onClose}>
                        {isExit ? 'กลับไปแก้ไข' : 'ยกเลิก'}
                    </button>
                    <button
                        className="confirm-btn ok"
                        onClick={onConfirm}
                    >
                        {isExit ? 'บันทึก' : 'ตกลง'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Main MiniPOS Page Component
export default function MiniPosPage() {
    const navigate = useNavigate();
    const user = useStore((s) => s.user);
    const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
    const [cart, setCart] = useState<CartItem[]>([]);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmMode, setConfirmMode] = useState<'enter' | 'exit'>('enter');
    const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [activeId, setActiveId] = useState<number | null>(null);

    // DnD Sensors - long press to drag
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 200,
                tolerance: 5,
            }
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250,
                tolerance: 5,
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    // Load menu items
    const loadMenuItems = useCallback(async () => {
        if (!user?.userId) {
            // No user - just show empty state, user can still add items in demo mode
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        try {
            const result = await api.request<MenuItem[]>(`/api/menu/${user.userId}`);
            if (result.data) {
                setMenuItems(result.data);
            }
        } catch (error) {
            console.error('Error loading menu:', error);
        } finally {
            setIsLoading(false);
        }
    }, [user?.userId]);

    useEffect(() => {
        loadMenuItems();
    }, [loadMenuItems]);

    // Handle drag start
    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id as number);
    };

    // Handle drag end
    const handleDragEnd = async (event: DragEndEvent) => {
        setActiveId(null);
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = menuItems.findIndex((item) => item.id === active.id);
        const newIndex = menuItems.findIndex((item) => item.id === over.id);
        const newItems = arrayMove(menuItems, oldIndex, newIndex);
        setMenuItems(newItems);

        // Save new order to API
        if (user?.userId) {
            try {
                await api.request(`/api/menu/reorder/${user.userId}`, {
                    method: 'PUT',
                    body: JSON.stringify({ item_ids: newItems.map((i) => i.id) }),
                });
            } catch (error) {
                console.error('Error saving order:', error);
            }
        }
    };

    // Handle image upload
    const handleImageUpload = async (file: File): Promise<string | null> => {
        if (!user?.userId) return null;

        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('line_user_id', user.userId);

            const response = await fetch(
                `https://tangtungdev-api.greenslime2009.workers.dev/api/menu/upload`,
                {
                    method: 'POST',
                    body: formData,
                }
            );

            if (!response.ok) {
                throw new Error('Upload failed');
            }

            const result = await response.json();
            return result.url;
        } catch (error) {
            console.error('Error uploading image:', error);
            Toast.show({ content: 'อัปโหลดรูปไม่สำเร็จ', icon: 'fail' });
            return null;
        } finally {
            setIsUploading(false);
        }
    };

    // Handle menu item selection
    const handleSelectItem = (item: MenuItem) => {
        const existingIndex = cart.findIndex((c) => c.menuItem.id === item.id);
        if (existingIndex >= 0) {
            const newCart = [...cart];
            newCart[existingIndex].quantity += 1;
            setCart(newCart);
        } else {
            setCart([...cart, { menuItem: item, quantity: 1 }]);
        }
    };

    // Handle quantity change
    const handleChangeQuantity = (itemId: number, delta: number) => {
        const newCart = cart
            .map((c) => {
                if (c.menuItem.id === itemId) {
                    return { ...c, quantity: c.quantity + delta };
                }
                return c;
            })
            .filter((c) => c.quantity > 0);
        setCart(newCart);
    };

    // Handle add/edit menu
    const handleSaveMenu = async (name: string, price: number, imageUrl: string | null) => {
        if (!user?.userId) return;

        try {
            if (editingItem) {
                await api.request(`/api/menu/${editingItem.id}`, {
                    method: 'PUT',
                    body: JSON.stringify({ name, price, image_url: imageUrl }),
                });
            } else {
                await api.request('/api/menu', {
                    method: 'POST',
                    body: JSON.stringify({
                        line_user_id: user.userId,
                        name,
                        price,
                        image_url: imageUrl,
                    }),
                });
            }
            setIsModalOpen(false);
            setEditingItem(null);
            loadMenuItems();
            Toast.show({ content: editingItem ? 'บันทึกแล้ว' : 'เพิ่มเมนูแล้ว', icon: 'success' });
        } catch (error) {
            console.error('Error saving menu:', error);
            Toast.show({ content: 'เกิดข้อผิดพลาด', icon: 'fail' });
        }
    };

    // Handle edit menu
    const handleEditMenu = (item: MenuItem) => {
        setEditingItem(item);
        setIsModalOpen(true);
    };

    // Handle delete menu
    const handleDeleteMenu = async (item: MenuItem) => {
        const confirmed = await Dialog.confirm({
            content: `ลบ "${item.name}" ?`,
            confirmText: 'ลบ',
            cancelText: 'ยกเลิก',
        });

        if (!confirmed) return;

        try {
            await api.request(`/api/menu/${item.id}`, { method: 'DELETE' });
            loadMenuItems();
            Toast.show({ content: 'ลบแล้ว', icon: 'success' });
        } catch (error) {
            console.error('Error deleting menu:', error);
        }
    };

    // Calculate total
    const total = cart.reduce((sum, c) => sum + c.menuItem.price * c.quantity, 0);

    // Handle create QR
    const handleCreateQR = () => {
        if (total > 0) {
            useStore.getState().setCalcResult(total, true);
            navigate('/qr');
        }
    };

    // Get quantity for item
    const getQuantity = (itemId: number) => {
        const cartItem = cart.find((c) => c.menuItem.id === itemId);
        return cartItem?.quantity || 0;
    };

    // Get active item for drag overlay
    const activeItem = activeId ? menuItems.find((i) => i.id === activeId) : null;

    // Handle toggle edit mode
    const handleToggleEditMode = () => {
        if (!isEditMode) {
            // Enter edit mode
            setConfirmMode('enter');
            setShowConfirmModal(true);
        } else {
            // Exit edit mode
            setConfirmMode('exit');
            setShowConfirmModal(true);
        }
    };

    const handleConfirmEditMode = () => {
        if (confirmMode === 'enter') {
            setIsEditMode(true);
        } else {
            setIsEditMode(false);
        }
        setShowConfirmModal(false);
    };

    return (
        <div className="minipos-page">
            <SafeArea position="top" />

            {/* Header */}
            <div className="minipos-header">
                <div className="minipos-title">
                    🍽️ Mini POS
                </div>
                <button
                    className={`minipos-edit-btn ${isEditMode ? 'active' : ''}`}
                    onClick={handleToggleEditMode}
                    style={{
                        background: '#5CA8FE',
                        color: 'white',
                        border: 'none',
                        boxShadow: 'none'
                    }}
                >
                    <Edit2 size={18} style={{ marginRight: 6 }} />
                    {isEditMode ? 'เสร็จสิ้น' : 'แก้ไข'}
                </button>
            </div>

            {/* Menu Grid */}
            <div className="minipos-content">
                {isLoading ? (
                    <div className="empty-state">
                        <div className="loading-spinner"></div>
                        <div className="empty-state-text">กำลังโหลด...</div>
                    </div>
                ) : menuItems.length === 0 && !isEditMode ? (
                    <div className="empty-state">
                        <img
                            src={tangtungChef}
                            alt="น้องตังค์ตุงเชฟ"
                            style={{
                                width: 180,
                                height: 180,
                                objectFit: 'contain',
                                marginBottom: 24,
                                mixBlendMode: 'multiply',
                                maskImage: 'radial-gradient(circle at center, black 70%, transparent 100%)',
                                WebkitMaskImage: 'radial-gradient(circle at center, black 70%, transparent 100%)'
                            }}
                        />
                        <div className="empty-state-text">
                            ยังไม่มีเมนู<br />
                            <span style={{ fontSize: '14px', color: '#9CA3AF' }}>กด "แก้ไข" เพื่อเริ่มเพิ่มเมนูโปรดของร้าน</span>
                        </div>
                    </div>
                ) : (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext items={menuItems.map((i) => i.id)} strategy={rectSortingStrategy}>
                            <div className="menu-grid">
                                {menuItems.map((item) => (
                                    <SortableMenuCard
                                        key={item.id}
                                        item={item}
                                        isEditMode={isEditMode}
                                        isSelected={getQuantity(item.id) > 0}
                                        quantity={getQuantity(item.id)}
                                        onSelect={() => handleSelectItem(item)}
                                        onDelete={() => handleDeleteMenu(item)}
                                        onEdit={() => handleEditMenu(item)}
                                    />
                                ))}
                                {/* Add Menu Card */}
                                {isEditMode && (
                                    <div
                                        className="add-menu-card"
                                        onClick={() => { setEditingItem(null); setIsModalOpen(true); }}
                                    >
                                        <div className="add-menu-card-icon">
                                            <Plus size={32} color="var(--primary)" />
                                        </div>
                                        <span className="add-menu-card-text">เพิ่มเมนู</span>
                                    </div>
                                )}
                            </div>
                        </SortableContext>

                        {/* Drag Overlay for smooth animation */}
                        <DragOverlay adjustScale={false}>
                            {activeItem ? <DragOverlayCard item={activeItem} /> : null}
                        </DragOverlay>
                    </DndContext>
                )}
            </div>

            {/* Cart Section */}
            {cart.length > 0 && !isEditMode && (
                <div className="minipos-cart">
                    <div className="cart-header">
                        <div className="cart-title">🛒 ตะกร้า ({cart.length} รายการ)</div>
                        <div className="cart-clear" onClick={() => setCart([])}>ล้าง</div>
                    </div>
                    <div className="cart-items">
                        {cart.map((c) => (
                            <div key={c.menuItem.id} className="cart-item">
                                <div className="cart-item-image">
                                    {c.menuItem.image_url ? (
                                        <img src={c.menuItem.image_url} alt={c.menuItem.name} />
                                    ) : '🍽️'}
                                </div>
                                <div className="cart-item-info">
                                    <div className="cart-item-name">{c.menuItem.name}</div>
                                    <div className="cart-item-price">฿{(c.menuItem.price * c.quantity).toLocaleString()}</div>
                                </div>
                                <div className="cart-item-qty">
                                    <button className="qty-btn" onClick={() => handleChangeQuantity(c.menuItem.id, -1)}>−</button>
                                    <span className="qty-value">{c.quantity}</span>
                                    <button className="qty-btn" onClick={() => handleChangeQuantity(c.menuItem.id, 1)}>+</button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="cart-total">
                        <span className="total-label">ยอดรวม</span>
                        <span className="total-value">฿{total.toLocaleString()}</span>
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            {!isEditMode && (
                <div className="minipos-actions">
                    <button
                        className="action-btn secondary"
                        onClick={() => navigate('/calculator')}
                    >
                        <Calculator size={24} style={{ marginRight: 8 }} />
                        คิดเลข
                    </button>
                    <button
                        className="action-btn primary"
                        onClick={handleCreateQR}
                        disabled={total === 0}
                    >
                        <QrCode size={24} style={{ marginRight: 8 }} />
                        สร้าง QR ({total.toLocaleString()}฿)
                    </button>
                </div>
            )}

            <SafeArea position="bottom" />

            {/* Add/Edit Menu Modal */}
            <AddMenuModal
                isOpen={isModalOpen}
                onClose={() => { setIsModalOpen(false); setEditingItem(null); }}
                onSave={handleSaveMenu}
                onImageUpload={handleImageUpload}
                editItem={editingItem}
                isUploading={isUploading}
            />

            {/* Confirmation Modal */}
            <EditConfirmModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={handleConfirmEditMode}
                mode={confirmMode}
            />
        </div>
    );
}
